import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

// Helper: Strip HTML tags
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "");
};

// Enhanced Job Card Component
const JobCard = ({ job, onApply }) => {
  const plainDesc = stripHtml(job.description);
  const shortDesc =
    plainDesc.length > 120 ? plainDesc.substring(0, 120) + "…" : plainDesc;

  return (
    <div className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-red-200">
      {/* Gradient accent bar */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-500 to-red-600 rounded-l-2xl"></div>

      <div className="p-6 pl-7">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h3 className="text-xl font-bold text-gray-800 group-hover:text-red-600 transition-colors">
              {job.roleName}
            </h3>
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
              {job.salary && (
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {job.salary}
                </span>
              )}
              {job.experience && (
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {job.experience}
                </span>
              )}
              {job.vacancies && (
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  {job.vacancies} {job.vacancies === 1 ? "opening" : "openings"}
                </span>
              )}
            </div>
          </div>
        </div>

        {shortDesc && (
          <p className="text-gray-600 text-sm mt-3 leading-relaxed line-clamp-2">
            {shortDesc}
          </p>
        )}

        <div className="mt-5 flex justify-end">
          <button
            onClick={() => onApply(job)}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-full transition-all shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
          >
            Apply now
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Application Modal (styled consistently)
const ApplicationModal = ({ job, isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    applicantName: "",
    email: "",
    phone: "",
    resume: null,
    applicantDetails: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resumeFileName, setResumeFileName] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        setError("Please upload a PDF, DOC, or DOCX file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }
      setFormData((prev) => ({ ...prev, resume: file }));
      setResumeFileName(file.name);
      setError("");
    }
  };

  const uploadToCloudinary = async (file) => {
    const cloudinaryUrl = process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    if (!cloudinaryUrl || !uploadPreset)
      throw new Error("Cloudinary configuration missing");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);
    const response = await axios.post(cloudinaryUrl, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.applicantName.trim())
      return setError("Please enter your name");
    if (!formData.email.trim()) return setError("Please enter your email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      return setError("Valid email required");
    if (!formData.phone.trim())
      return setError("Please enter your phone number");
    if (!formData.resume) return setError("Please upload your resume");

    try {
      setUploading(true);
      const uploadedResumeUrl = await uploadToCloudinary(formData.resume);
      setSubmitting(true);
      await onSubmit({
        jobPost: job._id,
        applicantName: formData.applicantName,
        email: formData.email,
        phone: formData.phone,
        resume: uploadedResumeUrl,
        applicantDetails: formData.applicantDetails,
      });
      setFormData({
        applicantName: "",
        email: "",
        phone: "",
        resume: null,
        applicantDetails: "",
      });
      setResumeFileName("");
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Submission failed"
      );
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold text-gray-800">
            Apply for {job?.roleName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={uploading || submitting}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="applicantName"
              value={formData.applicantName}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
              disabled={uploading || submitting}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
              disabled={uploading || submitting}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
              disabled={uploading || submitting}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resume (PDF/DOC/DOCX) *
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileChange}
              className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              disabled={uploading || submitting}
              required
            />
            {resumeFileName && (
              <p className="text-xs text-green-600 mt-1">📎 {resumeFileName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional details (Optional)
            </label>
            <textarea
              name="applicantDetails"
              value={formData.applicantDetails}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
              placeholder="Share anything you'd like us to know..."
              disabled={uploading || submitting}
            />
          </div>
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-2 rounded-xl text-sm">
              {error}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition"
              disabled={uploading || submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition disabled:opacity-50"
              disabled={uploading || submitting}
            >
              {uploading
                ? "Uploading..."
                : submitting
                ? "Submitting..."
                : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Career = () => {
  const { jobName } = useParams();
  const [categories, setCategories] = useState([]);
  const [jobsByCategory, setJobsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchedCategory, setMatchedCategory] = useState(null);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [catRes, jobRes] = await Promise.all([
          axios.get(`${API_BASE}/job-category/get-all`),
          axios.get(`${API_BASE}/job-post/get-all`),
        ]);
        if (catRes.data.success && catRes.data.categories)
          setCategories(catRes.data.categories);
        else throw new Error("Failed to load categories");
        if (jobRes.data.success && jobRes.data.jobPosts) {
          const grouped = {};
          jobRes.data.jobPosts.forEach((job) => {
            const catId = job.category?._id;
            if (catId) {
              if (!grouped[catId]) grouped[catId] = [];
              grouped[catId].push(job);
            }
          });
          setJobsByCategory(grouped);
        } else throw new Error("Failed to load jobs");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API_BASE]);

  useEffect(() => {
    if (!loading && categories.length > 0) {
      const found = categories.find(
        (cat) => cat.name.toLowerCase() === jobName?.toLowerCase()
      );
      setMatchedCategory(found || null);
      setMatchedJobs(found ? jobsByCategory[found._id] || [] : []);
    }
  }, [categories, jobsByCategory, jobName, loading]);

  const handleApply = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleSubmitApplication = async (applicationData) => {
    const response = await axios.post(
      `${API_BASE}/job-application/create`,
      applicationData,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!response.data.success)
      throw new Error(response.data.message || "Submission failed");
    setSubmitSuccess(true);
    setTimeout(() => setSubmitSuccess(false), 3000);
    return response.data;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  if (loading) {
    return (
      <div className=" bg-gradient-to-br from-gray-50 to-white pt-16">
        <div className="px-4 md:px-8 lg:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-8 w-40 bg-gray-200 rounded mb-6"></div>
            <div className="h-12 w-3/4 bg-gray-200 rounded mb-4"></div>
            <div className="h-5 w-1/2 bg-gray-200 rounded mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow-sm p-6 h-32"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className=" flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-md max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Oops, something went wrong
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-500 text-white px-6 py-2 rounded-full hover:bg-red-600 transition"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!matchedCategory && !loading) {
    return (
      <div className=" bg-gradient-to-br from-gray-50 to-white pt-16">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="text-red-400 text-6xl mb-4">🔍</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Category not found
            </h1>
            <p className="text-gray-500 mb-6">
              We couldn't find a job category matching "{jobName}".
            </p>
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 bg-red-500 text-white px-6 py-2 rounded-full hover:bg-red-600 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Browse all categories
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className=" bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-8 pb-16">
        {submitSuccess && (
          <div className="fixed top-6 right-6 z-50 bg-green-500 text-white px-5 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in-out">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            Application submitted successfully!
          </div>
        )}

        <div className="px-4 md:px-8 lg:px-12">
          {/* Back button */}
          <div className="mb-8">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-1 text-gray-500 hover:text-red-500 transition font-medium"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Go Back
            </button>
          </div>

          {/* Hero header */}
          <div className="mb-12">
            <div className="inline-block px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-semibold uppercase tracking-wide mb-3">
              Careers
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
              {matchedCategory.name}
            </h1>
            {matchedCategory.description && (
              <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
                {matchedCategory.description}
              </p>
            )}
            <div className="flex items-center gap-3 mt-5">
              <div className="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-700">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-medium">{matchedJobs.length}</span>{" "}
                {matchedJobs.length === 1 ? "position" : "positions"} available
              </div>
            </div>
          </div>

          {/* Job listings */}
          {matchedJobs.length > 0 ? (
            <div className="space-y-5">
              {matchedJobs.map((job) => (
                <JobCard key={job._id} job={job} onApply={handleApply} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-gray-300 text-5xl mb-4">📭</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-1">
                No open positions right now
              </h3>
              <p className="text-gray-500 mb-6">
                We don't have any active jobs in this category at the moment.
              </p>
              <Link
                to="/careers"
                className="text-red-500 hover:text-red-600 font-medium inline-flex items-center gap-1"
              >
                Explore other categories
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>

      <ApplicationModal
        job={selectedJob}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmitApplication}
      />

      <style jsx>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default Career;

// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";

// // Static features data (kept as in original design)
// const features = [
//   {
//     title: "Health & Wellness",
//     desc: "Comprehensive health benefits, mental wellness support, and flexible time off to help you feel your best at work and beyond.",
//     icon: (
//       <svg
//         className="w-5 h-5 text-blue-400"
//         fill="none"
//         stroke="currentColor"
//         viewBox="0 0 24 24"
//       >
//         <path
//           strokeLinecap="round"
//           strokeLinejoin="round"
//           strokeWidth="2"
//           d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
//         ></path>
//       </svg>
//     ),
//   },
//   {
//     title: "Recognition & Rewards",
//     desc: "We celebrate great work with performance bonuses, team recognition programs, and opportunities to grow your impact.",
//     icon: (
//       <svg
//         className="w-5 h-5 text-blue-400"
//         fill="none"
//         stroke="currentColor"
//         viewBox="0 0 24 24"
//       >
//         <path
//           strokeLinecap="round"
//           strokeLinejoin="round"
//           strokeWidth="2"
//           d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
//         ></path>
//       </svg>
//     ),
//   },
//   {
//     title: "Learning & Growth",
//     desc: "Access mentorship, training programs, and career development resources designed to help you reach your full potential.",
//     icon: (
//       <svg
//         className="w-5 h-5 text-blue-400"
//         fill="none"
//         stroke="currentColor"
//         viewBox="0 0 24 24"
//       >
//         <path
//           strokeLinecap="round"
//           strokeLinejoin="round"
//           strokeWidth="2"
//           d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
//         ></path>
//       </svg>
//     ),
//   },
//   {
//     title: "Work-Life Balance",
//     desc: "Flexible schedules, remote-friendly culture, and supportive teams that help you maintain balance every day of the week.",
//     icon: (
//       <svg
//         className="w-5 h-5 text-blue-400"
//         fill="none"
//         stroke="currentColor"
//         viewBox="0 0 24 24"
//       >
//         <path
//           strokeLinecap="round"
//           strokeLinejoin="round"
//           strokeWidth="2"
//           d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//         ></path>
//       </svg>
//     ),
//   },
// ];

// // Reusable Job Card – receives real job data and onApply callback
// const JobCard = ({ job, onApply }) => {
//   // Strip HTML tags from description for a clean snippet
//   const plainDesc = job.description
//     ? job.description.replace(/<[^>]*>/g, "")
//     : "";
//   const shortDesc =
//     plainDesc.length > 100 ? plainDesc.substring(0, 100) + "…" : plainDesc;

//   return (
//     <div className="bg-white p-6 shadow-sm mb-4 min-h-[140px] relative">
//       <div className="w-full">
//         <h3 className="text-md font-semibold text-gray-800 mb-1">
//           {job.roleName}
//         </h3>
//         <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
//           {job.salary && <span>💰 {job.salary}</span>}
//           {job.experience && <span>⏱️ {job.experience}</span>}
//           {job.vacancies && <span>👥 {job.vacancies} vacancies</span>}
//         </div>
//         {shortDesc && <p className="text-gray-600 text-sm mb-2">{shortDesc}</p>}
//       </div>
//       <button
//         onClick={() => onApply(job)}
//         className="absolute bottom-2 right-6 bg-[#ff4a4a] hover:bg-red-600 text-white text-[10px] uppercase tracking-wider font-semibold px-5 py-2 rounded transition-colors"
//       >
//         Apply Now
//       </button>
//     </div>
//   );
// };

// // Vertical side tab – kept for reference (not used in main return)
// const VerticalTab = ({ title, isActive, onClick }) => (
//   <div
//     className={`bg-[#fcf5f5] rounded-md w-16 md:w-20 flex items-center justify-center min-h-[400px] cursor-pointer transition-all duration-200 ${
//       isActive ? "border-l-4 border-red-500" : "hover:bg-gray-100"
//     }`}
//     onClick={onClick}
//   >
//     <span className="font-heading transform -rotate-90 whitespace-nowrap tracking-wide text-gray-800 uppercase text-sm font-medium">
//       {title}
//     </span>
//   </div>
// );

// // Application Modal Component
// const ApplicationModal = ({ job, isOpen, onClose, onSubmit }) => {
//   const [formData, setFormData] = useState({
//     applicantName: "",
//     email: "",
//     phone: "",
//     resume: null,
//     applicantDetails: "",
//   });
//   const [uploading, setUploading] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const [error, setError] = useState("");
//   const [resumeUrl, setResumeUrl] = useState("");
//   const [resumeFileName, setResumeFileName] = useState("");

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//     setError("");
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       // Validate file type (PDF, DOC, DOCX)
//       const validTypes = [
//         "application/pdf",
//         "application/msword",
//         "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
//       ];
//       if (!validTypes.includes(file.type)) {
//         setError("Please upload a PDF, DOC, or DOCX file");
//         return;
//       }
//       // Validate file size (max 5MB)
//       if (file.size > 5 * 1024 * 1024) {
//         setError("File size must be less than 5MB");
//         return;
//       }
//       setFormData((prev) => ({ ...prev, resume: file }));
//       setResumeFileName(file.name);
//       setError("");
//     }
//   };

//   const uploadToCloudinary = async (file) => {
//     const cloudinaryUrl = process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL;
//     const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

//     if (!cloudinaryUrl || !uploadPreset) {
//       throw new Error("Cloudinary configuration missing");
//     }

//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", uploadPreset);

//     const response = await axios.post(cloudinaryUrl, formData, {
//       headers: { "Content-Type": "multipart/form-data" },
//     });
//     return response.data.secure_url;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError("");

//     // Validate required fields
//     if (!formData.applicantName.trim()) {
//       setError("Please enter your name");
//       return;
//     }
//     if (!formData.email.trim()) {
//       setError("Please enter your email");
//       return;
//     }
//     if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
//       setError("Please enter a valid email address");
//       return;
//     }
//     if (!formData.phone.trim()) {
//       setError("Please enter your phone number");
//       return;
//     }
//     if (!formData.resume) {
//       setError("Please upload your resume");
//       return;
//     }

//     try {
//       setUploading(true);
//       // Upload resume to Cloudinary
//       const uploadedResumeUrl = await uploadToCloudinary(formData.resume);
//       setResumeUrl(uploadedResumeUrl);

//       setSubmitting(true);
//       // Submit application
//       await onSubmit({
//         jobPost: job._id,
//         applicantName: formData.applicantName,
//         email: formData.email,
//         phone: formData.phone,
//         resume: uploadedResumeUrl,
//         applicantDetails: formData.applicantDetails,
//       });

//       // Reset form and close modal on success
//       setFormData({
//         applicantName: "",
//         email: "",
//         phone: "",
//         resume: null,
//         applicantDetails: "",
//       });
//       setResumeFileName("");
//       setResumeUrl("");
//       onClose();
//     } catch (err) {
//       console.error("Application submission error:", err);
//       setError(
//         err.response?.data?.message ||
//           err.message ||
//           "Failed to submit application. Please try again."
//       );
//     } finally {
//       setUploading(false);
//       setSubmitting(false);
//     }
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
//       <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
//         <div className="flex justify-between items-center p-4 border-b">
//           <h2 className="text-base font-semibold text-gray-800">
//             Apply for {job?.roleName}
//           </h2>
//           <button
//             onClick={onClose}
//             className="text-gray-500 hover:text-gray-700 transition-colors"
//             disabled={uploading || submitting}
//           >
//             <svg
//               className="w-6 h-6"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth="2"
//                 d="M6 18L18 6M6 6l12 12"
//               ></path>
//             </svg>
//           </button>
//         </div>

//         <form onSubmit={handleSubmit} className="p-4 space-y-4">
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Full Name *
//             </label>
//             <input
//               type="text"
//               name="applicantName"
//               value={formData.applicantName}
//               onChange={handleChange}
//               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
//               placeholder="Enter your full name"
//               disabled={uploading || submitting}
//               required
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Email Address *
//             </label>
//             <input
//               type="email"
//               name="email"
//               value={formData.email}
//               onChange={handleChange}
//               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
//               placeholder="you@example.com"
//               disabled={uploading || submitting}
//               required
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Phone Number *
//             </label>
//             <input
//               type="tel"
//               name="phone"
//               value={formData.phone}
//               onChange={handleChange}
//               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
//               placeholder="9876543210"
//               disabled={uploading || submitting}
//               required
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Resume (PDF, DOC, DOCX) *
//             </label>
//             <input
//               type="file"
//               accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
//               onChange={handleFileChange}
//               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
//               disabled={uploading || submitting}
//               required
//             />
//             {resumeFileName && (
//               <p className="text-xs text-green-600 mt-1">
//                 Selected: {resumeFileName}
//               </p>
//             )}
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Additional Details (Optional)
//             </label>
//             <textarea
//               name="applicantDetails"
//               value={formData.applicantDetails}
//               onChange={handleChange}
//               rows="3"
//               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
//               placeholder="Tell us about your experience, skills, or anything else you'd like us to know..."
//               disabled={uploading || submitting}
//             />
//           </div>

//           {error && (
//             <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
//               {error}
//             </div>
//           )}

//           <div className="flex gap-3 pt-2">
//             <button
//               type="button"
//               onClick={onClose}
//               className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
//               disabled={uploading || submitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               className="flex-1 px-4 py-2 bg-[#ff4a4a] text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//               disabled={uploading || submitting}
//             >
//               {uploading
//                 ? "Uploading Resume..."
//                 : submitting
//                 ? "Submitting..."
//                 : "Submit Application"}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// const Career = () => {
//   const [categories, setCategories] = useState([]);
//   const [jobsByCategory, setJobsByCategory] = useState({});
//   const [selectedCategoryId, setSelectedCategoryId] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Modal state
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [submitSuccess, setSubmitSuccess] = useState(false);

//   // Carousel state & refs for features section
//   const [activeIndex, setActiveIndex] = useState(0);
//   const carouselRef = useRef(null);
//   const autoScrollInterval = useRef(null);
//   const [isUserInteracting, setIsUserInteracting] = useState(false);

//   const API_BASE = process.env.REACT_APP_API_URL;

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
//         const [catRes, jobRes] = await Promise.all([
//           axios.get(`${API_BASE}/job-category/get-all`),
//           axios.get(`${API_BASE}/job-post/get-all`),
//         ]);

//         if (catRes.data.success && catRes.data.categories) {
//           setCategories(catRes.data.categories);
//         } else {
//           throw new Error("Failed to load categories");
//         }

//         if (jobRes.data.success && jobRes.data.jobPosts) {
//           const grouped = {};
//           jobRes.data.jobPosts.forEach((job) => {
//             const catId = job.category?._id;
//             if (catId) {
//               if (!grouped[catId]) grouped[catId] = [];
//               grouped[catId].push(job);
//             }
//           });
//           setJobsByCategory(grouped);
//         } else {
//           throw new Error("Failed to load job posts");
//         }
//       } catch (err) {
//         console.error(err);
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [API_BASE]);

//   // Auto-select first category when data loads
//   useEffect(() => {
//     if (categories.length > 0 && !selectedCategoryId) {
//       setSelectedCategoryId(categories[0]._id);
//     }
//   }, [categories, selectedCategoryId]);

//   // --- Carousel Auto-scroll Logic (only for mobile) ---
//   const startAutoScroll = () => {
//     if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
//     autoScrollInterval.current = setInterval(() => {
//       if (!isUserInteracting && carouselRef.current && window.innerWidth < 768) {
//         const nextIndex = (activeIndex + 1) % features.length;
//         const scrollAmount = nextIndex * carouselRef.current.clientWidth;
//         carouselRef.current.scrollTo({ left: scrollAmount, behavior: "smooth" });
//         setActiveIndex(nextIndex);
//       }
//     }, 5000);
//   };

//   const stopAutoScroll = () => {
//     if (autoScrollInterval.current) {
//       clearInterval(autoScrollInterval.current);
//       autoScrollInterval.current = null;
//     }
//   };

//   const handleScroll = () => {
//     if (!carouselRef.current) return;
//     const scrollLeft = carouselRef.current.scrollLeft;
//     const cardWidth = carouselRef.current.clientWidth;
//     const newIndex = Math.round(scrollLeft / cardWidth);
//     if (newIndex !== activeIndex && newIndex >= 0 && newIndex < features.length) {
//       setActiveIndex(newIndex);
//     }
//   };

//   // Set up carousel event listeners and auto-scroll on mount/unmount
//   useEffect(() => {
//     if (window.innerWidth < 768) {
//       startAutoScroll();
//       const ref = carouselRef.current;
//       if (ref) {
//         ref.addEventListener("scroll", handleScroll);
//         const onTouchStart = () => setIsUserInteracting(true);
//         const onTouchEnd = () => setTimeout(() => setIsUserInteracting(false), 3000);
//         ref.addEventListener("touchstart", onTouchStart);
//         ref.addEventListener("touchend", onTouchEnd);
//         ref.addEventListener("mousedown", onTouchStart);
//         ref.addEventListener("mouseup", onTouchEnd);
//         return () => {
//           stopAutoScroll();
//           ref.removeEventListener("scroll", handleScroll);
//           ref.removeEventListener("touchstart", onTouchStart);
//           ref.removeEventListener("touchend", onTouchEnd);
//           ref.removeEventListener("mousedown", onTouchStart);
//           ref.removeEventListener("mouseup", onTouchEnd);
//         };
//       }
//     } else {
//       stopAutoScroll();
//     }
//   }, [activeIndex, isUserInteracting]);

//   useEffect(() => {
//     return () => stopAutoScroll();
//   }, []);

//   const handleApply = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };

//   const handleSubmitApplication = async (applicationData) => {
//     const response = await axios.post(
//       `${API_BASE}/job-application/create`,
//       applicationData,
//       {
//         headers: { "Content-Type": "application/json" },
//       }
//     );

//     if (!response.data.success) {
//       throw new Error(response.data.message || "Application submission failed");
//     }

//     setSubmitSuccess(true);
//     setTimeout(() => setSubmitSuccess(false), 3000);
//     return response.data;
//   };

//   const closeModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };

//   const selectedCategory = categories.find((c) => c._id === selectedCategoryId);
//   const selectedJobs = selectedCategoryId
//     ? jobsByCategory[selectedCategoryId] || []
//     : [];
//   const otherCategories = categories.filter(
//     (c) => c._id !== selectedCategoryId
//   );

//   // Loading State
//   if (loading) {
//     return (
//       <div className="bg-white mt-12">
//         <div className="flex flex-col md:flex-row gap-4">
//           <div className="flex-1 bg-[#fcf5f5] rounded-md p-6 min-h-[500px]">
//             <div className="h-6 bg-gray-300 w-40 mb-6 animate-pulse"></div>
//             {[1, 2, 3].map((i) => (
//               <div
//                 key={i}
//                 className="bg-white p-6 shadow-sm mb-4 min-h-[140px] animate-pulse"
//               >
//                 <div className="h-4 bg-gray-300 w-3/4 mb-3"></div>
//                 <div className="h-3 bg-gray-300 w-1/2 mb-4"></div>
//                 <div className="flex gap-2">
//                   <div className="h-2 bg-gray-300 w-10"></div>
//                   <div className="h-2 bg-gray-300 w-10"></div>
//                 </div>
//               </div>
//             ))}
//           </div>
//           <div className="flex gap-4 hidden sm:flex">
//             {[1, 2, 3].map((i) => (
//               <div
//                 key={i}
//                 className="bg-[#fcf5f5] rounded-md w-16 md:w-20 flex items-center justify-center min-h-[400px] animate-pulse"
//               >
//                 <span className="transform -rotate-90 whitespace-nowrap">
//                   ...
//                 </span>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="max-w-6xl mx-auto p-4 md:p-8 font-sans bg-white text-center">
//         <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
//           Error: {error}
//         </div>
//         <button
//           onClick={() => window.location.reload()}
//           className="bg-[#ff4a4a] text-white px-4 py-2 rounded"
//         >
//           Retry
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white mt-12">
//       {/* Success Toast */}
//       {submitSuccess && (
//         <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg animate-fade-in-out">
//           Application submitted successfully!
//         </div>
//       )}

//       {/* Main Content Layout - Job Accordion (unchanged) */}
//       <div className="px-4 md:px-8 lg:px-12 pb-8 lg:pb-12">
//         {/* DESKTOP: Row-wise accordion (horizontal layout) */}
//         <div className="hidden md:flex flex-row gap-0 border border-gray-200 rounded-md overflow-hidden">
//           {categories.map((category) => {
//             const isOpen = selectedCategoryId === category._id;
//             const jobs = jobsByCategory[category._id] || [];

//             return (
//               <div
//                 key={category._id}
//                 className={`transition-all duration-500 ease-in-out flex flex-col ${
//                   isOpen ? "flex-[4]" : "flex-[1]"
//                 }`}
//                 style={{ minWidth: 0 }}
//               >
//                 {/* Header / Tab Button */}
//                 <button
//                   onClick={() => setSelectedCategoryId(category._id)}
//                   className={`flex items-center justify-center w-full cursor-pointer border-r border-gray-200 transition-colors duration-200 ${
//                     isOpen
//                       ? "bg-white py-4 px-6 justify-start border-b border-gray-200"
//                       : "bg-[#fcf5f5] hover:bg-[#fbeeee] py-0"
//                   }`}
//                   style={{
//                     minHeight: isOpen ? "auto" : "420px",
//                     writingMode: isOpen ? "horizontal-tb" : "vertical-rl",
//                     transform: isOpen ? "none" : "rotate(180deg)",
//                   }}
//                 >
//                   <span
//                     className={`font-semibold tracking-wide uppercase text-sm whitespace-nowrap ${
//                       isOpen ? "text-[#cc2222]" : "text-gray-700"
//                     }`}
//                   >
//                     {category.name}
//                   </span>
//                 </button>

//                 {/* Expanded Body */}
//                 {isOpen && (
//                   <div className="bg-white p-6 flex-1 overflow-y-auto max-h-[520px]">
//                     <div className="flex flex-col gap-3">
//                       {jobs.length === 0 ? (
//                         <p className="text-gray-400 text-sm py-8 text-center">
//                           No openings in this category yet.
//                         </p>
//                       ) : (
//                         jobs.map((job) => (
//                           <JobCard
//                             key={job._id}
//                             job={job}
//                             onApply={handleApply}
//                           />
//                         ))
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>

//         {/* MOBILE: Column-wise accordion (vertical stacked) */}
//         <div className="flex md:hidden flex-col border border-gray-200 rounded-md overflow-hidden">
//           {categories.map((category) => {
//             const isOpen = selectedCategoryId === category._id;
//             const jobs = jobsByCategory[category._id] || [];

//             return (
//               <div
//                 key={category._id}
//                 className="border-b border-gray-200 last:border-b-0"
//               >
//                 {/* Row Header */}
//                 <button
//                   onClick={() =>
//                     setSelectedCategoryId(isOpen ? null : category._id)
//                   }
//                   className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 ${
//                     isOpen
//                       ? "bg-white border-b border-gray-100"
//                       : "bg-[#fcf5f5] hover:bg-[#fbeeee] "
//                   }`}
//                 >
//                   <span
//                     className={`font-semibold text-sm uppercase tracking-wide ${
//                       isOpen ? "text-[#cc2222]" : "text-gray-700"
//                     }`}
//                   >
//                     {category.name}
//                   </span>
//                 </button>

//                 {/* Mobile Expanded Body */}
//                 {isOpen && (
//                   <div className="bg-white px-4 py-4">
//                     <div className="flex flex-col gap-3">
//                       {jobs.length === 0 ? (
//                         <p className="text-gray-400 text-sm py-6 text-center">
//                           No openings in this category yet.
//                         </p>
//                       ) : (
//                         jobs.map((job) => (
//                           <JobCard
//                             key={job._id}
//                             job={job}
//                             onApply={handleApply}
//                           />
//                         ))
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       {/* Application Modal */}
//       <ApplicationModal
//         job={selectedJob}
//         isOpen={isModalOpen}
//         onClose={closeModal}
//         onSubmit={handleSubmitApplication}
//       />

//       <style jsx>{`
//         /* Hide scrollbar for carousel */
//         .scrollbar-hide::-webkit-scrollbar {
//           display: none;
//         }
//         @keyframes fadeInOut {
//           0% {
//             opacity: 0;
//             transform: translateY(-10px);
//           }
//           10% {
//             opacity: 1;
//             transform: translateY(0);
//           }
//           90% {
//             opacity: 1;
//             transform: translateY(0);
//           }
//           100% {
//             opacity: 0;
//             transform: translateY(-10px);
//           }
//         }
//         .animate-fade-in-out {
//           animation: fadeInOut 3s ease-in-out;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default Career;
